/**
 * =========================================================================================
 * UForceLogout — blocker popup for "session terminated" broadcasts.
 *
 * Self-contained centered modal rendered through React portal at document.body
 * so it cannot be visually clipped by any parent's positioning / overflow.
 *
 * Mount once per app (preferably inside <UFrameworkProvider> when available):
 *     <UForceLogout isAccountApp={true} />   // account, admin
 *     <UForceLogout />                       // every other product app
 *
 * The component is also resilient when mounted OUTSIDE the provider — it
 * falls back to local React state.
 * =========================================================================================
 **/

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as Icon from 'ubs-icons';
import PropTypes from 'prop-types';

import UButton from '../UButton';
import { COLOR } from '../common/constant';
import { eraseCookie } from '../common/function';
import { useUFramework, useDispatchUFramework } from '../context';
import { FORCE_LOGOUT_ACTIONS, FORCE_LOGOUT_WINDOW_EVENT } from './constant';
import { getMySessionId, resolveLoginUrl, shouldForceLogout } from './helpers';
import './_force-logout.scss';

const PLATFORM_ICON = {
    WEB: Icon.Browser,
    MOBILE: Icon.Mobile,
    DESKTOP: Icon.Desktop,
    ELECTRON: Icon.Desktop,
};

const UForceLogout = (props) => {
    const isAccountApp = !!props.isAccountApp;
    const orgIdProp = props.orgId || null;

    const useSocketFromCtx = useUFramework((s) => (s ? s.useSocket : null));
    const socketId = useUFramework((s) => (s ? s.SOCKET_ID : null));
    const ctxData = useUFramework((s) => (s && s.forceLogout ? s.forceLogout.data : null));
    const ctxDispatch = useDispatchUFramework();

    const hasCtxDispatch = typeof ctxDispatch === 'function';
    const [localData, setLocalData] = useState(null);
    const data = hasCtxDispatch ? ctxData : localData;

    // JWT cookie is stable for the lifetime of this mount (it only changes on
    // login/logout, both of which unmount the component). Decode once.
    const mySessionId = useMemo(() => getMySessionId(), []);
    const overlayRef = useRef(null);

    const setForceLogout = useCallback((payload) => {
        if (hasCtxDispatch) ctxDispatch({ type: FORCE_LOGOUT_ACTIONS.SET, payload });
        else setLocalData((prev) => (prev ? prev : payload));
    }, [hasCtxDispatch, ctxDispatch]);

    const clearForceLogout = useCallback(() => {
        if (hasCtxDispatch) ctxDispatch({ type: FORCE_LOGOUT_ACTIONS.CLEAR });
        else setLocalData(null);
    }, [hasCtxDispatch, ctxDispatch]);

    const onResponse = useCallback((res) => {
        try {
            if (!shouldForceLogout(res, mySessionId)) return;
            setForceLogout(res.data);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[UForceLogout] listener error', e);
        }
    }, [setForceLogout, mySessionId]);

    // Primary channel — socket via context useSocket. Wait for SOCKET_ID
    // because child useEffects fire BEFORE the parent's socketConnect().
    useEffect(() => {
        if (typeof useSocketFromCtx !== 'function') {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[UForceLogout] useSocket not exposed in UFrameworkProvider value - '
                    + 'falling back to window-event channel. Add useSocket to the provider for real-time delivery.'
                );
            }
            return undefined;
        }
        if (!socketId) return undefined;
        let sub;
        try {
            sub = useSocketFromCtx(onResponse, 'forceLogout');
            // initialState no-op returns only { off, refresh } — no `on`. Real useSocket exposes `on` too.
            // If `on` is missing, the provider didn't wire useSocket through (e.g. admin app) — bail out
            // and let the window-event fallback do the work.
            if (!sub || typeof sub.on !== 'function') {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.warn(
                        '[UForceLogout] useSocket from context is a no-op stub - '
                        + 'falling back to window-event channel. Wire the real useSocket into UFrameworkProvider value.'
                    );
                }
                return undefined;
            }
            if (typeof sub.refresh === 'function') sub.refresh();
            else sub.on();
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[UForceLogout] failed to subscribe to socket', e);
            return undefined;
        }
        return () => {
            try { if (sub && typeof sub.off === 'function') sub.off(); } catch (e) { /* unsub must not throw */ }
        };
    }, [useSocketFromCtx, onResponse, socketId]);

    // Fallback channel — window event (always wired)
    useEffect(() => {
        const handler = (evt) => onResponse(evt && evt.detail);
        window.addEventListener(FORCE_LOGOUT_WINDOW_EVENT, handler);
        return () => window.removeEventListener(FORCE_LOGOUT_WINDOW_EVENT, handler);
    }, [onResponse]);

    // Body scroll lock + Escape suppression + focus to Done button while open.
    // User MUST click Done — Escape is intentionally captured-and-swallowed so it
    // does not bubble to other parent modals or default browser behaviour.
    useEffect(() => {
        if (!data) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKey = (e) => {
            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('keydown', onKey, true);

        // UButton is a plain functional component (no forwardRef) — query the
        // rendered DOM button by its class and focus it.
        try {
            const btn = overlayRef.current && overlayRef.current.querySelector('.u_force-logout-card__done');
            if (btn && typeof btn.focus === 'function') btn.focus();
        } catch (e) { /* focus must not throw */ }

        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey, true);
        };
    }, [data]);

    if (!data) return null;

    const session = data.session || {};
    const platform = String(session.platform || '').toUpperCase();
    const IconCmp = PLATFORM_ICON[platform] || Icon.Desktop;
    const deviceLabel = [session.device, session.browser].filter(Boolean).join(' - ') || 'Unknown device';
    const location = [session.city, session.country].filter(Boolean).join(', ');
    const bodyText = data.error || 'This device session has been terminated and is no longer active.';

    const onDone = () => {
        try { localStorage.clear(); } catch (e) { /* storage may be restricted */ }
        try { sessionStorage.clear(); } catch (e) { /* storage may be restricted */ }
        // Use eraseCookie (NOT deleteAllCookies) — eraseCookie expires the cookie across
        // every Superworks domain (superworks.com/app/net/localhost) with path=/, which is
        // the only way to actually kill the cross-subdomain `ubs_token`. deleteAllCookies
        // has no domain/path so it silently leaves ubs_token alive → after redirect the
        // account app auto-authenticates → backend re-emits forceLogout (loop).
        try {
            eraseCookie('ubs_token');
            eraseCookie('ubsTabId');
            eraseCookie('signup_token');
        } catch (e) { /* cookie clear must not block redirect */ }

        clearForceLogout();

        const target = resolveLoginUrl({ isAccountApp, orgId: orgIdProp });
        window.location.href = target;
    };

    const stopPropagation = (e) => e.stopPropagation();

    const modal = (
        <div ref={overlayRef} className="u_force-logout-overlay" role="dialog" aria-modal="true" aria-labelledby="u_force-logout-title">
            <div className="u_force-logout-card" onClick={stopPropagation}>
                <div className="u_force-logout-card__check">
                    <Icon.PowerButton size={28} fill={COLOR.u_blue} />
                </div>

                <h3 id="u_force-logout-title" className="u_force-logout-card__title">Signed out</h3>

                <p className="u_force-logout-card__body">{bodyText}</p>

                <div className="u_force-logout-card__device">
                    <IconCmp size={22} fill={COLOR.u_blue} />
                    <div className="u_force-logout-card__device-meta">
                        <p className="u_force-logout-card__device-label">{deviceLabel}</p>
                        {location && <p className="u_force-logout-card__device-line">{location}</p>}
                        <p className="u_force-logout-card__device-line">Terminated just now</p>
                    </div>
                </div>

                <UButton cn="u_force-logout-card__done " onClick={onDone} title={`Done`} />
            </div>
        </div>
    );

    // Portal to body so the overlay cannot be clipped or repositioned by any parent
    if (typeof document === 'undefined' || !document.body) return modal;
    return ReactDOM.createPortal(modal, document.body);
};

UForceLogout.propTypes = {
    isAccountApp: PropTypes.bool,
    orgId: PropTypes.string,
};

UForceLogout.defaultProps = {
    isAccountApp: false,
    orgId: null,
};

export default UForceLogout;
